/**
 * whiteboardSignifiers — apply a user-defined "visual language" to a captured
 * shape.
 *
 * A team can encode rich data in physical/digital cues — fill color, border
 * color, text color, the shape itself — and map each cue to a platform
 * attribute with a value (e.g. green fill → impact 10, diamond → category risk).
 * The mapping is the workspace's shared signifier rules (reused from the Miro
 * color-map config so a language is configured once). This module is the pure,
 * deterministic core: given a shape's signifiers and the rules, it resolves the
 * field assignments and folds them into an item patch.
 *
 * Targets are matched tolerantly because the field catalog and stored rules use
 * slightly different keys ('category' vs 'itemCategory', 'altitude' vs
 * 'altitudeLevel'). Known cues map to native item fields; anything else (e.g.
 * 'attribute:value') lands non-destructively in item metadata, which is exactly
 * how custom field values are stored.
 */

import type { MiroColorRule, SignifierDimension } from './fieldMappingRegistry';
import {
  CONNECTOR_SIGNIFIER_DIMENSIONS,
  isColorSignifierDimension,
  ruleDimension,
} from './fieldMappingRegistry';
import type { ItemLanguageType } from '../types/prioritization/lifecycle';
import type { AltitudeLevel } from '../types/prioritization/itemLifecycle';

/** The visual cues read off one shape, in normalized lowercase tokens. */
export interface ShapeSignifiers {
  fillColor?: string;
  borderColor?: string;
  textColor?: string;
  shape?: string;
}

/** One resolved assignment from a matched rule. */
export interface SignifierAssignment {
  field: string;
  /** Present → assign this value; absent → route the shape's text to the field. */
  value?: string;
  dimension: SignifierDimension;
}

const VALID_CATEGORIES: ReadonlySet<string> = new Set<ItemLanguageType>([
  'problem', 'opportunity', 'risk', 'solution', 'commitment', 'investment',
]);

function norm(s: string | undefined): string {
  return (s ?? '').toLowerCase().trim();
}

function signifierFor(sig: ShapeSignifiers, dim: SignifierDimension): string | undefined {
  switch (dim) {
    case 'fill_color':
    case 'shape_color':
      return sig.fillColor;
    case 'border_color': return sig.borderColor;
    case 'text_color': return sig.textColor;
    case 'shape': return sig.shape;
    case 'line_style':
    case 'line_color':
    case 'line_type':
    case 'arrow_color':
    case 'arrow_shape':
      return undefined; // connector-side; not a node attribute
  }
}

/**
 * Resolve which field assignments a shape earns under the given rules. A rule
 * matches when the shape's token in the rule's dimension equals the rule's
 * matched token (case-insensitive). Pure & deterministic.
 */
export function applySignifierRules(sig: ShapeSignifiers, rules: MiroColorRule[]): SignifierAssignment[] {
  const out: SignifierAssignment[] = [];
  for (const rule of rules) {
    const dim = ruleDimension(rule);
    if (CONNECTOR_SIGNIFIER_DIMENSIONS.has(dim)) continue;
    const token = norm(signifierFor(sig, dim));
    if (!token) continue;
    if (token !== norm(rule.color)) continue;
    out.push({ field: rule.field, value: rule.value, dimension: dim });
  }
  return out;
}

/** A patch the importer can fold onto a planned item. */
export interface SignifierItemPatch {
  itemCategory?: ItemLanguageType;
  altitudeLevel?: AltitudeLevel;
  /** Free-form attribute values (numbers where parseable), keyed by field. */
  metadata?: Record<string, string | number>;
}

const CATEGORY_KEYS = new Set(['itemcategory', 'category', 'item_category']);
const ALTITUDE_KEYS = new Set(['altitudelevel', 'altitude', 'altitude_level']);

/**
 * Fold value-bearing assignments into an item patch. Known cues (category,
 * altitude) map to native fields when the value is valid; everything else is
 * stored under metadata (numbers parsed). Text-routing rules (no value) are
 * ignored here — the shape's text is already the item title.
 */
export function assignmentsToItemPatch(assignments: SignifierAssignment[]): SignifierItemPatch {
  const patch: SignifierItemPatch = {};
  for (const a of assignments) {
    if (a.value == null || a.value === '') continue;
    const key = a.field.toLowerCase();
    if (CATEGORY_KEYS.has(key)) {
      const v = a.value.toLowerCase();
      if (VALID_CATEGORIES.has(v)) patch.itemCategory = v as ItemLanguageType;
      else (patch.metadata ??= {})[a.field] = a.value;
      continue;
    }
    if (ALTITUDE_KEYS.has(key)) {
      const n = Math.round(Number(a.value));
      if (Number.isFinite(n) && n >= 1 && n <= 5) patch.altitudeLevel = n as AltitudeLevel;
      else (patch.metadata ??= {})[a.field] = a.value;
      continue;
    }
    // Any other field (e.g. 'attribute:value', 'attribute:risk') → metadata,
    // numeric where it parses cleanly.
    const num = Number(a.value);
    (patch.metadata ??= {})[a.field] = a.value !== '' && Number.isFinite(num) ? num : a.value;
  }
  return patch;
}

/** Convenience: signifiers + rules → item patch in one call. */
export function resolveSignifierPatch(sig: ShapeSignifiers, rules: MiroColorRule[]): SignifierItemPatch {
  return assignmentsToItemPatch(applySignifierRules(sig, rules));
}

// ── Human-readable legend ────────────────────────────────────────────────────

const DIMENSION_PHRASE: Record<SignifierDimension, string> = {
  fill_color: 'fill',
  shape_color: 'shape color',
  border_color: 'border',
  text_color: 'text',
  shape: 'shape',
  line_style: 'connector',
  line_color: 'line color',
  line_type: 'line type',
  arrow_color: 'arrow color',
  arrow_shape: 'arrow shape',
};

function titleCase(token: string): string {
  return token.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Friendly name for a rule's target field (drops the 'attribute:' prefix etc.). */
function fieldPhrase(field: string): string {
  const key = field.toLowerCase();
  if (CATEGORY_KEYS.has(key)) return 'category';
  if (ALTITUDE_KEYS.has(key)) return 'altitude';
  if (key === 'relationshiptype') return 'relationship';
  return field.replace(/^attribute:/i, '');
}

/**
 * One human-readable line for a rule, for the import legend. Examples:
 *   "Green fill → impact = 10", "Diamond → category = risk",
 *   "Double Arrow connector → aggregation". Pure & deterministic.
 */
export function describeSignifierRule(rule: MiroColorRule): string {
  const dim = ruleDimension(rule);
  const token = titleCase(rule.color || '—');
  const isConnector = CONNECTOR_SIGNIFIER_DIMENSIONS.has(dim);
  const lhs = isColorSignifierDimension(dim) || dim === 'shape' || dim === 'line_type' || dim === 'arrow_shape'
    ? `${token} ${DIMENSION_PHRASE[dim]}`
    : `${token} connector`;
  if (isConnector) return `${lhs} → ${fieldPhrase(rule.field)}${rule.value ? ` ${rule.value}` : ''}`;
  if (rule.value) return `${lhs} → ${fieldPhrase(rule.field)} = ${rule.value}`;
  return `${lhs} → ${fieldPhrase(rule.field)}`;
}
